import React, { useRef, useState } from 'react';
import { PdfComponentType, PdfImageComponent, PdfImageFit, PdfRepeat } from '../PdfComponent';
import { usePdfTemplateContext } from '../PdfTemplateEditorContext';
import { PdfTemplateUtil } from '../pdfTemplate.util';
import { Rnd } from 'react-rnd';
import PdfTemplateStaticTextPreview from './PdfTemplateStaticTextPreview';
import PdfTemplateImagePreview from './PdfTemplateImagePreview';
import PdfTemplateTablePreview from './PdfTemplateTablePreview';
import PdfTemplateFieldPreview from './PdfTemplateFieldPreview';
import { v4 } from 'uuid';
import PdfTemplateBarcodePreview from './PdfTemplateBarcodePreview';

const { mmToPx, pxToMm, PAGE_SIZES, getPageSize } = PdfTemplateUtil;

interface PdfTemplateEditorCanvasProps {}

export default function PdfTemplateEditorCanvas<T>({}: PdfTemplateEditorCanvasProps) {
  const {
    documentProperties,
    components,
    setComponents,
    updateComponent,
    editorSettings,
    selectedId,
    setSelectedId,
    updateEditorSettings,
  } = usePdfTemplateContext<T>();

  const { gridMode, gridMm, zoom } = editorSettings;
  const { width: PAGE_WIDTH_MM, height: PAGE_HEIGHT_MM } = getPageSize(documentProperties);

  // Panning (drag the canvas background to move the page)
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef<{ startX: number; startY: number; originX: number; originY: number; dragging: boolean }>({
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    dragging: false,
  });

  const onBackgroundMouseDown = (e: React.MouseEvent) => {
    // Only start panning when clicking outside of components
    const target = e.target as HTMLElement;
    const clickedComponent = target.closest('[data-template-component]');
    // If clicking on a component, do not start background panning
    if (clickedComponent) return;
    // Clicking on page or outside should clear selection
    setSelectedId(null);
    // Allow panning
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: pan.x,
      originY: pan.y,
      dragging: true,
    };
    setIsDragging(true);
  };

  const onBackgroundMouseMove = (e: React.MouseEvent | MouseEvent) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    // Clamp panning to reasonable bounds to avoid infinite drift
    const pageW = mmToPx(PAGE_WIDTH_MM) * zoom;
    const pageH = mmToPx(PAGE_HEIGHT_MM) * zoom;
    const limitX = pageW * 0.75; // allow up to 0.75x page width in any direction
    const limitY = pageH * 0.75; // allow up to 0.75x page height in any direction
    const nextX = dragState.current.originX + dx;
    const nextY = dragState.current.originY + dy;
    setPan({ x: Math.max(-limitX, Math.min(limitX, nextX)), y: Math.max(-limitY, Math.min(limitY, nextY)) });
  };

  const onBackgroundMouseUp = () => {
    dragState.current.dragging = false;
    setIsDragging(false);
  };

  return (
    <div
      className='h-full w-full overflow-hidden'
      onWheel={e => {
        // Ctrl/Cmd + wheel adjusts zoom; plain wheel pans
        if (e.ctrlKey || e.metaKey) {
          // e.preventDefault();
          // deltaY > 0 -> zoom out, < 0 -> zoom in
          const step = 0.05; // finer control via wheel
          let next = e.deltaY > 0 ? zoom - step : zoom + step;
          // Clamp strictly to [0.5, 4.0]
          if (next < 0.5) next = 0.5;
          if (next > 4) next = 4;
          updateEditorSettings({ zoom: next });
          return;
        }
        const pageW = mmToPx(PAGE_WIDTH_MM) * zoom;
        const pageH = mmToPx(PAGE_HEIGHT_MM) * zoom;
        const limitX = pageW * 0.75;
        const limitY = pageH * 0.75;
        const nextX = pan.x - e.deltaX; // natural feel: horizontal scroll moves canvas opposite
        const nextY = pan.y - e.deltaY; // vertical scroll pans up/down
        setPan({ x: Math.max(-limitX, Math.min(limitX, nextX)), y: Math.max(-limitY, Math.min(limitY, nextY)) });
      }}
      onMouseDown={onBackgroundMouseDown}
      onMouseMove={onBackgroundMouseMove}
      onMouseUp={onBackgroundMouseUp}
      onMouseLeave={onBackgroundMouseUp}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <div className='h-full w-full'>
        <div className='flex h-full w-full items-center justify-center'>
          <div className='flex w-full flex-col items-center'>
            <div
              className='relative bg-white shadow-default'
              style={{
                width: mmToPx(PAGE_WIDTH_MM) * zoom,
                height: mmToPx(PAGE_HEIGHT_MM) * zoom,
                transform: `translate(${pan.x}px, ${pan.y}px)`,
                backgroundSize: gridMode ? `${mmToPx(gridMm) * zoom}px ${mmToPx(gridMm) * zoom}px` : undefined,
                backgroundImage: gridMode
                  ? 'linear-gradient(to right, #e5e5e5 1px, transparent 1px), linear-gradient(to bottom, #e5e5e5 1px, transparent 1px)'
                  : undefined,
              }}
              data-editor-page
              onDragOver={e => {
                if (e.dataTransfer?.types?.includes('Files')) {
                  e.preventDefault();
                }
              }}
              onDrop={e => {
                const file = e.dataTransfer?.files?.[0];
                e.preventDefault();
                // Determine drop position relative to this canvas
                const rect = e.currentTarget.getBoundingClientRect();
                const dropXpx = e.clientX - rect.left;
                const dropYpx = e.clientY - rect.top;
                const dropXmm = pxToMm((dropXpx - pan.x) / zoom);
                const dropYmm = pxToMm((dropYpx - pan.y) / zoom);
                const defaultWmm = 50;
                const defaultHmm = 30;
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUrl = String(reader.result);
                  const imgEl = new Image();
                  imgEl.onload = () => {
                    const newComp: PdfImageComponent = {
                      repeatMode: PdfRepeat.always,
                      repeat: false,
                      id: v4(),
                      type: PdfComponentType.image,
                      x: dropXmm - defaultWmm / 2,
                      y: dropYmm - defaultHmm / 2,
                      width: defaultWmm,
                      height: defaultHmm,
                      src: dataUrl,
                      fit: PdfImageFit.contain,
                      opacity: 1,
                    };
                    setComponents([newComp, ...components]);
                    setSelectedId(newComp.id);
                  };
                  imgEl.src = dataUrl;
                };
                reader.readAsDataURL(file);
              }}
            >
              {components.map(comp => {
                if (comp.repeatMode === PdfRepeat.attachment) return null;
                const { componentBoxPx, gridSizePx, pxToMmScaled, snapMm } = usePdfTemplateContext<T>();
                const box = componentBoxPx(comp);
                const gridPx = gridSizePx();

                const isSelected = comp.id === selectedId;

                const preview = editorSettings.previewPage;
                const appliesToPreview = (() => {
                  switch (comp.repeatMode) {
                    case PdfRepeat.always:
                      return true;
                    case PdfRepeat.first:
                      return preview === 'first';
                    case PdfRepeat.last:
                      return preview === 'last';
                  }
                })();

                return (
                  <Rnd
                    key={comp.id}
                    bounds='parent'
                    size={{ width: box.width, height: box.height }}
                    position={{ x: box.x, y: box.y }}
                    dragGrid={gridMode ? [gridPx, gridPx] : undefined}
                    enableResizing={isSelected}
                    resizeGrid={gridMode ? [gridPx, gridPx] : undefined}
                    // Mark wrapper so background panning can ignore component interactions
                    data-template-component
                    onDragStart={() => setSelectedId(comp.id)}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setSelectedId(comp.id);
                    }}
                    onDragStop={(e, d) => {
                      const xMm = pxToMmScaled(d.x);
                      const yMm = pxToMmScaled(d.y);
                      updateComponent(comp.id, {
                        x: gridMode ? snapMm(xMm) : xMm,
                        y: gridMode ? snapMm(yMm) : yMm,
                      });
                    }}
                    onResizeStop={(e, dir, ref, delta, pos) => {
                      const widthMm = pxToMmScaled(ref.offsetWidth);
                      const heightMm = pxToMmScaled(ref.offsetHeight);
                      const xMm = pxToMmScaled(pos.x);
                      const yMm = pxToMmScaled(pos.y);
                      updateComponent(comp.id, {
                        width: gridMode ? snapMm(widthMm) : widthMm,
                        height: gridMode ? snapMm(heightMm) : heightMm,
                        x: gridMode ? snapMm(xMm) : xMm,
                        y: gridMode ? snapMm(yMm) : yMm,
                      });
                    }}
                    className={`cursor-move ${
                      isSelected ? 'border border-blue-700' : 'hover:border hover:border-blue-300'
                    } bg-white`}
                    style={{
                      zIndex: components.length - components.indexOf(comp),
                      opacity: appliesToPreview ? 1 : 0.35,
                      pointerEvents: appliesToPreview ? undefined : 'none',
                    }}
                  >
                    {comp.type === PdfComponentType.staticText && <PdfTemplateStaticTextPreview component={comp} />}
                    {comp.type === PdfComponentType.table && <PdfTemplateTablePreview component={comp} />}
                    {comp.type === PdfComponentType.image && <PdfTemplateImagePreview component={comp} />}
                    {comp.type === PdfComponentType.field && <PdfTemplateFieldPreview component={comp} />}
                    {comp.type === PdfComponentType.barcode && <PdfTemplateBarcodePreview component={comp} />}
                  </Rnd>
                );
              })}
            </div>

            {components
              .filter(c => c.type === PdfComponentType.image && c.repeatMode === PdfRepeat.attachment)
              .map(comp => {
                if (comp.type !== PdfComponentType.image) return null;
                const { componentBoxPx, gridSizePx, pxToMmScaled, snapMm } = usePdfTemplateContext<T>();
                const box = componentBoxPx(comp);
                const gridPx = gridSizePx();
                const isSelected = comp.id === selectedId;
                return (
                  <div
                    key={`attachment-page-${comp.id}`}
                    className='relative mt-6 bg-white shadow-default'
                    style={{
                      width: mmToPx(PAGE_WIDTH_MM) * zoom,
                      height: mmToPx(PAGE_HEIGHT_MM) * zoom,
                      transform: `translate(${pan.x}px, ${pan.y}px)`,
                      backgroundSize: gridMode ? `${mmToPx(gridMm) * zoom}px ${mmToPx(gridMm) * zoom}px` : undefined,
                      backgroundImage: gridMode
                        ? 'linear-gradient(to right, #e5e5e5 1px, transparent 1px), linear-gradient(to bottom, #e5e5e5 1px, transparent 1px)'
                        : undefined,
                    }}
                    data-editor-page
                  >
                    <Rnd
                      key={comp.id}
                      bounds='parent'
                      size={{ width: box.width, height: box.height }}
                      position={{ x: box.x, y: box.y }}
                      dragGrid={gridMode ? [gridPx, gridPx] : undefined}
                      enableResizing={isSelected}
                      resizeGrid={gridMode ? [gridPx, gridPx] : undefined}
                      // Mark wrapper so background panning can ignore component interactions
                      data-template-component
                      onDragStart={() => setSelectedId(comp.id)}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setSelectedId(comp.id);
                      }}
                      onDragStop={(e, d) => {
                        const xMm = pxToMmScaled(d.x);
                        const yMm = pxToMmScaled(d.y);
                        updateComponent(comp.id, {
                          x: gridMode ? snapMm(xMm) : xMm,
                          y: gridMode ? snapMm(yMm) : yMm,
                        });
                      }}
                      onResizeStop={(e, dir, ref, delta, pos) => {
                        const widthMm = pxToMmScaled(ref.offsetWidth);
                        const heightMm = pxToMmScaled(ref.offsetHeight);
                        const xMm = pxToMmScaled(pos.x);
                        const yMm = pxToMmScaled(pos.y);
                        updateComponent(comp.id, {
                          width: gridMode ? snapMm(widthMm) : widthMm,
                          height: gridMode ? snapMm(heightMm) : heightMm,
                          x: gridMode ? snapMm(xMm) : xMm,
                          y: gridMode ? snapMm(yMm) : yMm,
                        });
                      }}
                      className={`cursor-move ${
                        isSelected ? 'border border-blue-700' : 'hover:border hover:border-blue-300'
                      } bg-white`}
                      style={{
                        zIndex: components.length - components.indexOf(comp),
                        opacity: 1,
                      }}
                    >
                      <PdfTemplateImagePreview component={comp} />
                    </Rnd>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
